import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';

import HeaderSelect from './HeaderSelect';
import MenuOptions from './MenuOptions';

import { SearchVilla } from '@/actions/villages';
import ShortListModalContent from '@/components/ShortListModalContent';
import GlobalLoader from '../GlobalLoader';
import Input from '../Input';
import LoginModal from '../LoginModal';
import { SearchResultsContainer } from '../NewHeader/SearchResultsContainer';

import { useTranslation } from '@/contexts/dictionaryContext';
import useGlobalContext from '@/hooks/useGlobalContext';
import useUserContext from '@/hooks/useUserContext';
import { cn } from '@/lib/utils';
import Link from 'next/link';

import logoBlue from '../../../public/logo-desktop.svg';
import logoWhite from '../../../public/white-desktop-logo.svg';
import UserAvatar from '../UserAvatar';
import { useLocale } from '@/hooks/useLocale';

interface HeaderPortraitProps {
  setShowVilla: Dispatch<SetStateAction<boolean>>;
  setShowGuide: Dispatch<SetStateAction<boolean>>;
  register: UseFormRegister<FieldValues>;
  isLoading: boolean;
  hasError: boolean;
  results: SearchVilla[];
  searchValue: string;
  lastSearchedQuery: string | null;
  isActive: boolean;
  openMenu: boolean;
  setOpenMenu: Dispatch<SetStateAction<boolean>>;
  clearSearch: () => void;
}

const HeaderPortrait: FC<HeaderPortraitProps> = ({
  setShowVilla,
  setShowGuide,
  register,
  isLoading,
  hasError,
  results,
  searchValue,
  lastSearchedQuery,
  isActive,
  openMenu,
  setOpenMenu,
  clearSearch,
}) => {
  const { isLogged } = useUserContext();
  const { isHeaderActive, setIsHeaderActive } = useGlobalContext();
  const { header, modals } = useTranslation();
  const lang = useLocale();

  const isAtTheTopOfHomePage = !isActive && !openMenu;
  const headerIsFixedAndClosed = isActive && !openMenu;

  const pathname = usePathname();

  const handleSetOpenMenu = () => {
    if (openMenu) {
      clearSearch();
    }
    setOpenMenu(!openMenu);
    setIsHeaderActive(!isHeaderActive);
  };

  useEffect(() => {
    setOpenMenu(false);
  }, [pathname, setOpenMenu]);

  return (
    <div
      className={cn(
        `w-full h-full desktop:hidden flex flex-col z-[999]`,
        isHeaderActive && 'bg-white',
      )}>
      <div className="flex items-center justify-between h-[100px] px-[15px] sm:px-10">
        <div className="flex items-center justify-between flex-1">
          <button
            id="menu-button"
            aria-label="Open menu"
            className="relative w-[27px] h-[20px] ml-[8px]"
            onClick={handleSetOpenMenu}>
            <span
              className={cn(
                'absolute top-0 left-0 h-[2px] w-full transition-all duration-300',
                {
                  'bg-black rotate-45 top-[9px]': openMenu,
                  'bg-black': headerIsFixedAndClosed,
                  'bg-white': isAtTheTopOfHomePage,
                },
              )}></span>
            <span
              className={cn(
                'absolute top-[45%] left-0 h-[2px] w-full transition-all duration-300',
                {
                  'opacity-0': openMenu,
                  'bg-black': headerIsFixedAndClosed,
                  'bg-white': isAtTheTopOfHomePage,
                },
              )}></span>
            <span
              className={cn(
                'absolute bottom-0 left-0 h-[2px] w-full transition-all duration-300',
                {
                  'bg-black -rotate-45 bottom-[9px]': openMenu,
                  'bg-black': headerIsFixedAndClosed,
                  'bg-white': isAtTheTopOfHomePage,
                },
              )}></span>
          </button>

          <Link href={lang == 'en' ? '/' : `/${lang}`}>
            <Image
              priority={true}
              src={isActive ? logoBlue : logoWhite}
              alt="logo"
              width={225}
              height={40}
            />
          </Link>

          <ul className="flex justify-end">
            {isLogged && (
              <li className="px-[8px] relative">
                <UserAvatar.Container withAnchor>
                  <UserAvatar.Root />
                </UserAvatar.Container>
              </li>
            )}
            <li className="px-[8px]">
              <ShortListModalContent />
            </li>
          </ul>
        </div>
      </div>
      {openMenu && (
        <div className="fixed inset-x-0 top-[100px] flex max-h-[calc(100svh-100px)] flex-col overflow-y-auto overscroll-contain bg-white shadow-[0_4px_5px_0_rgba(0,0,0,0.1)] px-4 pb-4 w-full gap-[20px] z-[1000] pt-5">
          <MenuOptions
            dict={header}
            lang={lang}
            pathname={pathname}
            setShowVilla={setShowVilla}
            setShowGuide={setShowGuide}
            className="!flex-initial"
          />

          <div>{!isLogged && <LoginModal dict={modals} isHeader />}</div>

          <HeaderSelect isActive={isActive} />

          <div className="w-full">
            <div className="flex">
              <Input.Container className="grow">
                <Input.Root
                  register={register}
                  name="search"
                  placeholder={header.search}
                  className="placeholder:text-gray-400 placeholder:text-sm"
                />
              </Input.Container>
            </div>
            <div
              className={cn(
                'relative w-full',
                isLoading && searchValue.length >= 3 && 'min-h-[120px]',
              )}>
              <SearchResultsContainer
                isLoading={isLoading}
                hasError={hasError}
                results={results}
                searchValue={searchValue}
                lastSearchedQuery={lastSearchedQuery}
                handleClose={() => setOpenMenu(false)}
                inFlow
              />
              <GlobalLoader
                isLoading={isLoading && searchValue.length >= 3}
                className="!absolute !z-30 !inset-0 !w-full !h-full !top-0 !left-0 !bg-white/80"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderPortrait;
